import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  UserRound, Mail, Phone, ArrowRight,
  ShieldCheck, CheckCircle2, Loader2
} from "lucide-react";
import toast from "react-hot-toast";

export default function CustomerRegister() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: ""
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);

  const update = (field, value) => {
    setForm(previous => ({ ...previous, [field]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim()
    };

    if (payload.first_name.length < 2 || payload.last_name.length < 2) {
      toast.error("Introdu numele și prenumele complet.");
      return;
    }

    if (payload.phone.length < 10) {
      toast.error("Introdu un număr de telefon valid.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/customer/register", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : "Înregistrarea nu a putut fi procesată."
        );
      }

      setSubmitted(true);
      toast.success("Cererea de înregistrare a fost procesată.");
    } catch (error) {
      toast.error(error.message || "A apărut o eroare.");
    } finally {
      setLoading(false);
    }
  };

  const resendActivation = async () => {
    if (resending || resendCooldown) return;

    setResending(true);

    try {
      const response = await fetch("/api/customer/resend-activation", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email.trim() })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : "Nu am putut procesa solicitarea."
        );
      }

      toast.success(
        "Dacă adresa este eligibilă, vei primi un email de activare."
      );

      setResendCooldown(true);
      window.setTimeout(() => {
        setResendCooldown(false);
      }, 300000);

    } catch (error) {
      toast.error(error.message || "A apărut o eroare.");
    } finally {
      setResending(false);
    }
  };

  const fields = [
    { name: "first_name", label: "Prenume", type: "text", icon: UserRound, placeholder: "Prenumele tău", autocomplete: "given-name", max: 100 },
    { name: "last_name", label: "Nume", type: "text", icon: UserRound, placeholder: "Numele tău", autocomplete: "family-name", max: 100 },
    { name: "email", label: "Adresă de email", type: "email", icon: Mail, placeholder: "email@exemplu.ro", autocomplete: "email", max: 254 },
    { name: "phone", label: "Telefon", type: "tel", icon: Phone, placeholder: "07xxxxxxxx", autocomplete: "tel", max: 20 }
  ];

  return (
    <main className="min-h-screen bg-[#080d18] text-white px-4 py-16 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[650px] h-[450px] rounded-full bg-[#d4af37]/10 blur-[120px] pointer-events-none" />

      <div className="relative max-w-xl mx-auto">
        <div className="text-center mb-9">
          <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
            <ShieldCheck size={30} className="text-[#e5c46a]" />
          </div>

          <div className="text-[#e5c46a] text-xs tracking-[0.3em] font-bold mb-4">
            MTC CLEANING SYSTEM
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold">
            Creează contul tău
          </h1>

          <p className="text-slate-400 mt-4 leading-relaxed">
            Înregistrează-te pentru a avea acces la serviciile
            și abonamentele Kirby Alba Iulia.
          </p>
        </div>

        <div className="bg-[#101b30] border border-[#d4af37]/20 rounded-3xl p-6 sm:p-9 shadow-2xl">
          {submitted ? (
            <div className="text-center py-9">
              <CheckCircle2 size={62} className="text-[#e5c46a] mx-auto mb-6" />
              <h2 className="text-2xl font-bold mb-4">
                Verifică-ți emailul
              </h2>
              <p className="text-slate-300 leading-relaxed mb-5">
                Dacă adresa este eligibilă, vei primi un email
                cu instrucțiunile de activare a contului.
              </p>
              <p className="text-slate-400 text-sm mb-8">
                Verifică și folderul Spam. Linkul de activare
                este valabil 24 de ore.
              </p>
              <button
                type="button"
                onClick={resendActivation}
                disabled={resending || resendCooldown}
                className="w-full mb-5 flex items-center justify-center gap-2 rounded-xl border border-[#d4af37]/40 bg-[#d4af37]/10 text-[#e5c46a] px-6 py-4 font-semibold hover:bg-[#d4af37]/20 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {resending ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Se trimite...
                  </>
                ) : resendCooldown ? (
                  "Poți solicita din nou peste 5 minute"
                ) : (
                  <>
                    <Mail size={18} />
                    Retrimite emailul de activare
                  </>
                )}
              </button>

              <Link
                to="/autentificare"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] text-[#10141b] px-7 py-4 font-bold hover:bg-[#f0d67d] transition"
              >
                Mergi la autentificare
                <ArrowRight size={18} />
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {fields.map(({ name, label, type, icon: Icon, placeholder, autocomplete, max }) => (
                  <div
                    key={name}
                    className={name === "email" || name === "phone" ? "sm:col-span-2" : ""}
                  >
                    <label htmlFor={name} className="block text-sm text-slate-300 mb-2">
                      {label}
                    </label>
                    <div className="relative">
                      <Icon
                        size={18}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#d4af37]"
                      />
                      <input
                        id={name}
                        type={type}
                        required
                        minLength={name === "phone" ? 10 : name === "email" ? undefined : 2}
                        maxLength={max}
                        autoComplete={autocomplete}
                        value={form[name]}
                        onChange={(e) => update(name, e.target.value)}
                        placeholder={placeholder}
                        className="w-full rounded-xl bg-[#0a1324] border border-white/10 py-4 pl-12 pr-4 text-white placeholder:text-slate-600 outline-none focus:border-[#d4af37] transition"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-[#d4af37]/15 bg-[#d4af37]/5 p-4 flex gap-3">
                <ShieldCheck size={20} className="text-[#e5c46a] shrink-0" />
                <p className="text-sm text-slate-300 leading-relaxed">
                  După activarea contului, te autentifici și introduci
                  codul primit de la agent în urma prezentării.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#b9912e] via-[#e5c46a] to-[#c59d38] text-[#10141b] py-4 font-bold disabled:opacity-60 hover:brightness-110 transition"
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Se procesează...
                  </>
                ) : (
                  <>
                    Creează cont
                    <ArrowRight size={20} />
                  </>
                )}
              </button>

              <p className="text-center text-sm text-slate-400">
                Ai deja cont?{" "}
                <Link to="/autentificare" className="text-[#e5c46a] font-semibold hover:underline">
                  Autentifică-te
                </Link>
              </p>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-slate-500 mt-7">
          MTC Cleaning System · Kirby Alba Iulia
        </p>
      </div>
    </main>
  );
}
